import { BookOpen, CalendarDays, Download, GraduationCap, Landmark, Mic, Presentation, type LucideIcon } from "lucide-react";
import { useI18n } from "@/i18n/translations";
import { localizedPdfLabel } from "@/components/Editorial";
import { tipoScheda, TIPI_LABEL } from "@/data/events";
import type { Iniziativa, TipoScheda } from "@/data/events";

const MESI_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const MESI_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** Icona rappresentativa della tipologia, per le schede senza locandina. */
const ICONA_TIPO: Record<TipoScheda, LucideIcon> = {
  convegno: Presentation,
  seminario: BookOpen,
  giornata: CalendarDays,
  lectio: GraduationCap,
  conferenza: Mic,
  iniziativa: Landmark,
};

/** Card di un'iniziativa d'archivio: data, titolo, testo verbatim del documento, attribuzione e documenti PDF. */
export const EventCard = ({ ev }: { ev: Iniziativa }) => {
  const { lang, t } = useI18n();
  const mesi = lang === "en" ? MESI_EN : MESI_IT;
  const tipo = tipoScheda(ev);
  const Icona = ICONA_TIPO[tipo];
  let data: string;
  if (!ev.date) data = t("att.senza_data");
  else if (ev.date.d && ev.date.m) data = `${ev.date.d} ${mesi[ev.date.m - 1]} ${ev.date.y}`;
  else if (ev.date.m) data = `${mesi[ev.date.m - 1]} ${ev.date.y}`;
  else data = String(ev.date.y);

  return (
    <article id={ev.id} className="grid scroll-mt-44 border border-borderc bg-cardwhite md:grid-cols-[220px_1fr]" data-testid={`event-card-${ev.id}`}>
      <div className="border-b border-borderc bg-mutedbg md:border-b-0 md:border-r">
        {ev.poster ? (
          <img src={ev.poster} alt={`Poster — ${lang === "en" && ev.title.en ? ev.title.en : ev.title.it}`} className="img-treated aspect-[3/4] h-full w-full object-cover" />
        ) : (
          <div className="relative flex aspect-[3/4] items-center justify-center overflow-hidden" data-testid={`event-placeholder-${ev.id}`}>
            <div className="absolute inset-3 border border-ink/30" aria-hidden="true" />
            <div className="absolute inset-[18px] border border-ink/15" aria-hidden="true" />
            <div className="relative flex flex-col items-center gap-4 px-6 text-center">
              <Icona className="h-24 w-24 text-ink" strokeWidth={1.5} aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink">{lang === "en" ? TIPI_LABEL[tipo].en : TIPI_LABEL[tipo].it}</span>
              <span className="font-serif text-xl font-bold tracking-tight text-ink">CESP</span>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-3">
          {ev.n && <span className="font-serif text-sm italic text-bordeaux">n. {ev.n}</span>}
          <span className="eyebrow text-bordeaux" data-testid={`event-date-${ev.id}`}>{data}</span>
          <span className="border border-borderc px-2 py-0.5 text-[11px] uppercase tracking-[0.14em] text-mutedtx" data-testid={`event-tipo-${ev.id}`}>
            {lang === "en" ? TIPI_LABEL[tipo].en : TIPI_LABEL[tipo].it}
          </span>
        </div>
        <h3 className="font-serif text-2xl font-medium leading-snug text-ink">{lang === "en" && ev.title.en ? ev.title.en : ev.title.it}</h3>
        {ev.description && (
          <p className="text-sm leading-relaxed text-bodytx/85">{lang === "en" && ev.description.en ? ev.description.en : ev.description.it}</p>
        )}
        {ev.notaDate && (
          <div className="border border-amber-500/40 bg-amber-50 px-3.5 py-2.5 text-[12px] leading-relaxed text-amber-900" data-testid={`event-nota-${ev.id}`}>
            <span className="mr-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              {t("common.nota_da_confermare")}
            </span>
            {lang === "en" && ev.notaDate.en ? ev.notaDate.en : ev.notaDate.it}
          </div>
        )}
        {ev.attribution && ev.attributionText && (
          <p className="mt-auto pt-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-ink/70" data-testid={`event-attribution-${ev.id}`}>
            {t(`att.org.${ev.attribution}`)} {ev.attributionText}
          </p>
        )}
        {ev.pdfs && ev.pdfs.length > 0 && (
          <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
            {ev.pdfs.map((pdf, i) => (
              <a
                key={i}
                href={pdf.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`event-pdf-${ev.id}-${i}`}
                className="link-underline inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-bordeaux"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {localizedPdfLabel(pdf.label, lang)}
              </a>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

